import { z } from 'zod';

import {
  Email,
  OrganizationId,
  UserId,
  VaultId,
  ProfileId,
  SubscriptionErrorKnownValue,
  SubscriptionError,
  LicenseType,
  UnlockTimeLimit,
  OrganizationType,
  PrimaryLoginDevice,
  MaintenanceTask,
  EntraUserId,
  GenerationId,
  RoleId,
  RoleType,
  ExportPermission,
  ChildOrganizationAccess,
  MaximumAssignableVaultPermission,
} from 'backend-client-web';

import { PersistableAuthenticator } from '../clientCoreParameters/authenticator/PersistableAuthenticator';
import {
  makeProtobufEnumSchema,
  makeExcludeValuesTransform,
  makeTypescriptEnumSchema,
} from '../enumSchemas';
import { OrganizationClientSettings } from '../organizationClientSettings/OrganizationClientSettings';
import { ParentOrganizationMetadata } from '../parentOrganizationMetadata/ParentOrganizationMetadata';
import { PasswordPolicy } from '../passwordPolicy/PasswordPolicy';
import { unlockTimeLimitSchema } from '../session/Session';
import { uint8ArraySchema } from '../uint8ArraySchema';
import {
  WithUnknownValuePlaceholder,
  makeUnknownValuePlaceholderSchema,
} from '../unknownValuePlaceholder';
import { undefinedFromNullish } from '../util/undefinedFromNullish';
import { nullUuid } from '../uuid';

import { PersistableProfile } from './PersistableProfile';
import { PersistableRole } from './PersistableRole';

interface PersistableOrganizationEntraSettings {
  enableUserCreate: boolean;
  userCreateRoleId: RoleId | null;
  userCreateSendEmail: boolean;
  enableUserDisconnect: boolean;
  enableFilterGroups: boolean;
  filterGroups: string[];
}

const PersistableOrganizationEntraSettings = z.object({
  enableUserCreate: z.boolean(),
  userCreateSendEmail: z.boolean(),
  userCreateRoleId: z.uuid().nullable().default(null),
  enableUserDisconnect: z.boolean(),
  enableFilterGroups: z.boolean(),
  filterGroups: z.array(z.string()),
});

interface PersistableOrganizationAdminInfo {
  adminProfile: PersistableProfile;
  entraTenantId?: string;
  entraPreferUserPrincipalName: boolean;
  entraSettings?: PersistableOrganizationEntraSettings;
  entraSynchronizedAt?: Date;
  csvIntegrationActive: boolean;
  googleWorkspaceIntegrationActive: boolean;
  trialEndDate?: Date;
  licenseType: WithUnknownValuePlaceholder<LicenseType>;
  parentOrganizationAdminProfileId: ProfileId | null;
  billingOrganizationId: OrganizationId | null;
}

const PersistableOrganizationAdminInfo = z.object({
  authenticator: PersistableAuthenticator.optional(),
  adminProfile: PersistableProfile,
  entraTenantId: z.string().optional(),
  entraPreferUserPrincipalName: z.boolean().default(false),
  entraSettings: PersistableOrganizationEntraSettings.optional(),
  entraSynchronizedAt: z.date().optional(),
  csvIntegrationActive: z.boolean().default(false),
  googleWorkspaceIntegrationActive: z.boolean().default(false),
  trialEndDate: z.date().optional(),
  licenseType: makeProtobufEnumSchema(LicenseType, LicenseType.UNKNOWN).default(LicenseType.NONE),
  parentOrganizationAdminProfileId: z.uuid().nullable().default(null),
  billingOrganizationId: z.uuid().nullable().default(null),
});

interface PersistableOrganizationProfile {
  profileId: ProfileId;
  isDelegatedAdmin: boolean;
  isService: boolean;
  isNotConnected: boolean;
  personalVaultId?: VaultId;
  invitedAt?: Date;
  preliminaryUserId?: UserId;
  startCode?: string;
  startCodeGeneratedAt?: Date;
  startCodeEmailSentAt?: Date;
  startCodeExpiredAt?: Date;
  lastActiveAt?: Date;
  primaryLoginDevice?: WithUnknownValuePlaceholder<PrimaryLoginDevice, undefined>;
  entraUserId?: EntraUserId;
  roleId: RoleId;
}

const PersistableOrganizationProfile = z.object({
  profileId: z.uuid(),
  isDelegatedAdmin: z.boolean().default(false),
  isService: z.boolean().default(false),
  isNotConnected: z.boolean().default(false),
  personalVaultId: z.uuid().optional(),
  invitedAt: z.date().optional(),
  preliminaryUserId: z.uuid().optional(),
  startCode: z.string().optional(),
  startCodeGeneratedAt: z.date().optional(),
  startCodeEmailSentAt: z.date().optional(),
  startCodeExpiredAt: z.date().optional(),
  lastActiveAt: z.date().optional(),
  primaryLoginDevice: makeProtobufEnumSchema(PrimaryLoginDevice, undefined).optional(),
  entraUserId: z.uuid().optional(),
  roleId: z.uuid().default(nullUuid),
});

interface ParentOrganizationInfo {
  id: OrganizationId;
  name: string;
  icon?: string;
  metadata?: ParentOrganizationMetadata;
}

const ParentOrganizationInfo = z.object({
  id: z.uuid(),
  name: z.string(),
  icon: z.string().optional(),
  metadata: ParentOrganizationMetadata.optional(),
});

export interface LoginSummaryVaultInfo {
  id: VaultId;
  generationId: GenerationId;
  messagePublicKey: Uint8Array;
}

const LoginSummaryVaultInfo = z.object({
  id: z.uuid(),
  generationId: z.uuid(),
  messagePublicKey: uint8ArraySchema,
});

interface PersistableOrganization {
  id: OrganizationId;
  name: Email;
  organizationType: WithUnknownValuePlaceholder<OrganizationType>;
  superUserId: UserId;
  adminProfileId: ProfileId;
  icon: string | undefined;
  restrictPersonalLogins: boolean;
  auditlogEnable: boolean;
  roleManagementEnable: boolean;
  childOrganizationManagementEnable: boolean;
  monitoringEnable: boolean;
  profileChildOrganizationManagementShow: boolean;
  serviceUserEnable: boolean;
  isDelegatedAdmin: boolean;
  isPreliminary: boolean;
  profiles: Array<PersistableOrganizationProfile>;
  adminInfo?: PersistableOrganizationAdminInfo;
  subscriptionError:
    | WithUnknownValuePlaceholder<SubscriptionErrorKnownValue, undefined>
    | undefined;
  verifiedDomain: string | undefined;
  // dropped in favor of parentOrganizationInfo.id
  // parentOrganizationId: OrganizationId | null;
  passwordPolicy: WithUnknownValuePlaceholder<PasswordPolicy, { type: 'unknown' }>;
  unlockTimeLimit: UnlockTimeLimit;
  clientSettings: OrganizationClientSettings | undefined;
  parentOrganizationInfo?: ParentOrganizationInfo;
  requiredMaintenanceTasks?: WithUnknownValuePlaceholder<MaintenanceTask>[];
  entraUserId?: EntraUserId;
  loginSummaryVaultInfo?: LoginSummaryVaultInfo;
  roleId: RoleId;
  roles: Record<RoleId, PersistableRole>;
}

const schemaSubscriptionErrorKnownValue = makeProtobufEnumSchema(
  SubscriptionError,
  undefined,
).transform(makeExcludeValuesTransform(SubscriptionError.OK));

const PersistableOrganization = z.object({
  id: z.uuid(),
  name: z.string(),
  icon: undefinedFromNullish(z.string()),
  organizationType: makeProtobufEnumSchema(OrganizationType, OrganizationType.UNKNOWN).default(
    OrganizationType.UNKNOWN,
  ),
  restrictPersonalLogins: z.boolean().default(false),
  auditlogEnable: z.boolean().default(false),
  roleManagementEnable: z.boolean().default(false),
  childOrganizationManagementEnable: z.boolean().default(false),
  monitoringEnable: z.boolean().default(false),
  profileChildOrganizationManagementShow: z.boolean().default(false),
  serviceUserEnable: z.boolean().default(false),
  superUserId: z.uuid(),
  adminProfileId: z.uuid(),
  isDelegatedAdmin: z.boolean().default(false),
  isPreliminary: z.boolean().default(false),
  profiles: z.array(PersistableOrganizationProfile).default([]),
  adminInfo: PersistableOrganizationAdminInfo.optional(),
  subscriptionError: undefinedFromNullish(schemaSubscriptionErrorKnownValue),
  verifiedDomain: undefinedFromNullish(z.string()),
  passwordPolicy: makeUnknownValuePlaceholderSchema(PasswordPolicy, {
    type: 'unknown' as const,
  }).default({
    type: 'none',
    // Use a constant default in the past for the default password policy. The actual changeTime
    // doesn't matter at this point, because we're not going to perform rechecks based on an empty
    // password policy
    changeTime: '2025-08-01T00:00:00.000Z',
  }),
  unlockTimeLimit: unlockTimeLimitSchema.default({ enabled: false, minutes: 60 }),
  clientSettings: undefinedFromNullish(OrganizationClientSettings),
  parentOrganizationInfo: ParentOrganizationInfo.optional(),
  requiredMaintenanceTasks: z
    .array(makeTypescriptEnumSchema(MaintenanceTask, MaintenanceTask.UNKNOWN))
    .optional(),
  entraUserId: z.uuid().optional(),
  loginSummaryVaultInfo: LoginSummaryVaultInfo.optional(),
  roleId: z.uuid().default(nullUuid),
  // Roles in the persistable default to the nullUuid. We also default the
  // roles record to include an "empty role".
  roles: z.record(z.uuid(), PersistableRole).default({
    [nullUuid]: {
      isPrivileged: false,
      roleType: RoleType.USER,
      changedAt: new Date(),
      id: nullUuid,
      permissions: {
        adminCanAssignTeamPermissionSelf: MaximumAssignableVaultPermission.NONE,
        adminCanAssignTeamPermissionOther: MaximumAssignableVaultPermission.NONE,
        adminCanChangeSettings: false,
        canConnectUsers: false,
        adminCanManageOrganization: false,
        adminCanViewForeignPersonalVaults: false,
        canChangePlan: false,
        childOrganizationAccess: ChildOrganizationAccess.NONE,
        canCreateChildOrganizations: false,
        canCreateTeams: false,
        canViewAuditlog: false,
        canViewMonitoring: false,
        exportVaults: ExportPermission.NONE,
        canAccessPersonalVault: true,
        canViewUsers: false,
      },
    },
  }),
});

export {
  PersistableOrganization,
  PersistableOrganizationAdminInfo,
  ParentOrganizationInfo,
  PersistableOrganizationEntraSettings,
  PersistableOrganizationProfile,
};
